import { MavMessage } from "./MavMessage";

export interface MavMessageParser<T> {
  msgId: MavMessage;
  parse(payload: Uint8Array): T;
}

// === HEARTBEAT ===
export interface HeartbeatMessage {
  type: number;
  autopilot: number;
  baseMode: number;
  customMode: number;
  systemStatus: number;
}
export const Heartbeat: MavMessageParser<HeartbeatMessage> = {
  msgId: MavMessage.HEARTBEAT,
  parse(payload) {
    return {
      type: payload[0],
      autopilot: payload[1],
      baseMode: payload[2],
      customMode: payload[3],
      systemStatus: payload[4],
    };
  },
};

// === SYS_STATUS ===
export interface SysStatusMessage {
  voltageBattery: number;
  currentBattery: number;
  batteryRemaining: number;
}
export const SysStatus: MavMessageParser<SysStatusMessage> = {
  msgId: MavMessage.SYS_STATUS,
  parse(payload) {
    return {
      voltageBattery: payload[10] | (payload[11] << 8),
      currentBattery: payload[12] | (payload[13] << 8),
      batteryRemaining: payload[14],
    };
  },
};

// === ATTITUDE ===
export interface AttitudeMessage {
  roll: number;
  pitch: number;
  yaw: number;
  rollspeed: number;
  pitchspeed: number;
  yawspeed: number;
}
export const Attitude: MavMessageParser<AttitudeMessage> = {
  msgId: MavMessage.ATTITUDE,
  parse(payload) {
    const dv = new DataView(payload.buffer);
    return {
      roll: dv.getFloat32(0, true),
      pitch: dv.getFloat32(4, true),
      yaw: dv.getFloat32(8, true),
      rollspeed: dv.getFloat32(12, true),
      pitchspeed: dv.getFloat32(16, true),
      yawspeed: dv.getFloat32(20, true),
    };
  },
};

export interface GpsRawIntMessage {
  time_usec: bigint;   // uint64_t
  fix_type: number;    // uint8_t
  lat: number;         // int32_t (degrees * 1E7)
  lon: number;         // int32_t (degrees * 1E7)
  alt: number;         // int32_t (millimeters above MSL)
  eph: number;         // uint16_t (HDOP * 100)
  epv: number;         // uint16_t (VDOP * 100)
  vel: number;         // uint16_t (cm/s)
  cog: number;         // uint16_t (centidegrees)
  satellites_visible: number; // uint8_t
}

export const GpsRawInt: MavMessageParser<GpsRawIntMessage> = {
  msgId: MavMessage.GPS_RAW_INT,
  parse(payload) {
    const dv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
    return {
      time_usec: dv.getBigUint64(0, true),
      fix_type: dv.getUint8(8),
      lat: dv.getInt32(9, true),
      lon: dv.getInt32(13, true),
      alt: dv.getInt32(17, true),
      eph: dv.getUint16(21, true),
      epv: dv.getUint16(23, true),
      vel: dv.getUint16(25, true),
      cog: dv.getUint16(27, true),
      satellites_visible: dv.getUint8(29),
    };
  },
};

export interface ParamValueMessage {
  param_id: string;    // char[16]
  param_value: number; // float
  param_type: number;  // uint8_t (MAV_PARAM_TYPE)
  param_count: number; // uint16_t
  param_index: number; // uint16_t
}

export const ParamValue: MavMessageParser<ParamValueMessage> = {
  msgId: MavMessage.PARAM_VALUE,
  parse(payload) {
    const dv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
    const decoder = new TextDecoder("ascii");

    const param_id_bytes = payload.slice(0, 16);
    const param_id_raw = decoder.decode(param_id_bytes);
    const param_id = param_id_raw.replace(/\0.*$/, ""); // убрать \0 после строки

    return {
      param_id,
      param_value: dv.getFloat32(16, true),
      param_type: dv.getUint8(20),
      param_count: dv.getUint16(21, true),
      param_index: dv.getUint16(23, true),
    };
  },
};

export interface BatteryStatusMessage {
  id: number;                     // uint8_t   Battery ID
  battery_function: number;       // uint8_t   Function of the battery
  type: number;                   // uint8_t   Type (chemistry)
  temperature: number;            // int16_t   Temperature (cDegC)
  voltages: number[];             // uint16_t[10] Cell voltages in millivolts (0 = cell not used)
  current_battery: number;        // int16_t   Current in 10*mA (0 = unknown)
  current_consumed: number;       // int32_t   Consumed charge in mAh
  energy_consumed: number;        // int32_t   Consumed energy in hJ
  battery_remaining: number;      // int8_t    Remaining energy: (0%: empty, 100%: full)
  voltage: number;
}

export const BatteryStatus: MavMessageParser<BatteryStatusMessage> = {
  msgId: MavMessage.BATTERY_STATUS,

  parse(payload: Uint8Array): BatteryStatusMessage {
    const dv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
    let offset = 0;

    // значения в little-endian
    const current_consumed = dv.getInt32(offset, true); offset += 4;
    const energy_consumed = dv.getInt32(offset, true); offset += 4;

    const temperature = dv.getInt16(offset, true); offset += 2;

    const voltages: number[] = [];
    for (let i = 0; i < 10; i++) {
      voltages.push(dv.getUint16(offset, true));
      offset += 2;
    }

    const voltage = voltages.reduce((sum, a) => sum + (a !== 0xffff ? a / 1000 : 0), 0); //todo: fix, add voltages_ext

    const current_battery = dv.getInt16(offset, true); offset += 2;
    const id = dv.getUint8(offset++); 
    const battery_function = dv.getUint8(offset++);
    const type = dv.getUint8(offset++);
    const battery_remaining = dv.getInt8(offset++);

    return {
      id,
      battery_function,
      type,
      temperature,
      voltages,
      current_battery,
      current_consumed,
      energy_consumed,
      battery_remaining,
      voltage
    };
  },
};

// === VFR_HUD ===
export interface VfrHudMessage {
  airspeed: number;        // float — Воздушная скорость (м/с)
  groundspeed: number;     // float — Скорость относительно земли (м/с)
  heading: number;         // int16_t — Курс (градусы, 0–360)
  throttle: number;        // uint16_t — Газ (0–100%)
  alt: number;             // float — Высота над уровнем моря (м)
  climb: number;           // float — Скорость набора/снижения (м/с)
}

export const VfrHud: MavMessageParser<VfrHudMessage> = {
  msgId: MavMessage.VFR_HUD,
  parse(payload) {
    const dv = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);

    const airspeed = dv.getFloat32(0, true);
    const groundspeed = dv.getFloat32(4, true);
    const alt = dv.getFloat32(8, true);
    const climb = dv.getFloat32(12, true);
    const heading = dv.getInt16(16, true);
    const throttle = dv.getUint16(18, true);

    return {
      airspeed,
      groundspeed,
      heading,
      throttle,
      alt,
      climb,
    };
  },
};
